import { useState, useEffect } from 'react';
import { 
  ArrowRightLeft, 
  Users, 
  MapPin, 
  Calendar, 
  TrendingUp, 
  AlertCircle,
  Search,
  Filter
} from 'lucide-react';

interface Transfer {
  id: number;
  shipment_id: string;
  from_driver_name: string;
  to_driver_name: string;
  transfer_date: string;
  transfer_reason: string;
  transfer_location?: string;
  destination_changed: boolean;
  old_destination_city?: string;
  new_destination_city?: string;
  transferred_by_name?: string;
}

interface TransferStats {
  total_transfers: number;
  transfers_with_dest_change: number;
  transfer_reason: string;
  count_by_reason: number;
}

const AllTransfersPage = () => {
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [stats, setStats] = useState<TransferStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });

  const API_BASE_URL = import.meta.env?.VITE_BACKEND_URL;

  useEffect(() => {
    fetchAllTransfers();
    fetchStatistics();
  }, []);

 const fetchAllTransfers = async () => {
  try {
    setLoading(true);
    const token = localStorage.getItem('token');

    const response = await fetch(`${API_BASE_URL}/api/shipment_transfers/transfers/all`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include'
    });

    if (!response.ok) throw new Error('Eroare la încărcare');

    const data = await response.json();
    if (data.success) {
      setTransfers(data.transfers || []);
    }
  } catch (err) {
    console.error('Eroare:', err);
    setError('Nu s-au putut încărca transferurile');
  } finally {
    setLoading(false);
  }
};

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (dateFilter.start) params.append('startDate', dateFilter.start);
      if (dateFilter.end) params.append('endDate', dateFilter.end);

      const url = `${API_BASE_URL}/api/shipment_transfers/transfers/statistics${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Eroare la statistici');

      const data = await response.json();
      if (data.success) {
        setStats(data.statistics.summary || []);
      }
    } catch (err) {
      console.error('Eroare statistici:', err);
    }
  };

  const getReasonLabel = (reason: string): string => {
    const reasons: { [key: string]: string } = {
      'problema_vehicul': 'Problemă vehicul',
      'indisponibilitate_sofer': 'Șofer indisponibil',
      'schimbare_ruta': 'Schimbare rută',
      'optimizare': 'Optimizare',
      'alta': 'Alt motiv'
    };
    return reasons[reason] || reason;
  };

  const getReasonColor = (reason: string): string => {
    const colors: { [key: string]: string } = {
      'problema_vehicul': 'bg-red-100 text-red-800',
      'indisponibilitate_sofer': 'bg-yellow-100 text-yellow-800',
      'schimbare_ruta': 'bg-blue-100 text-blue-800',
      'optimizare': 'bg-green-100 text-green-800',
      'alta': 'bg-gray-100 text-gray-800'
    };
    return colors[reason] || 'bg-gray-100 text-gray-800';
  };

  const filteredTransfers = transfers.filter(t => {
    const matchesSearch = 
      t.shipment_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.from_driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.to_driver_name?.toLowerCase().includes(searchTerm.toLowerCase());

    if (dateFilter.start && dateFilter.end) {
      const transferDate = new Date(t.transfer_date);
      const start = new Date(dateFilter.start);
      const end = new Date(dateFilter.end);
      return matchesSearch && transferDate >= start && transferDate <= end;
    }

    return matchesSearch;
  });

  const totalTransfers = stats.reduce((sum, s) => sum + s.count_by_reason, 0);
  const transfersWithDestChange = stats.reduce((sum, s) => sum + (s.transfers_with_dest_change || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-3 text-sm text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 lg:space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-orange-600 flex-shrink-0" />
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 truncate">
            Istoric Transferuri
          </h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-600">Toate transferurile</p>
      </div>

      {/* Statistics Cards - Layout complet diferit pe mobil */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-3 lg:p-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between mb-1">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded flex items-center justify-center">
                <ArrowRightLeft className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
              </div>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-600 leading-tight">Total</p>
            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{totalTransfers}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-3 lg:p-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between mb-1">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-amber-100 rounded flex items-center justify-center">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600" />
              </div>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-600 leading-tight">Dest. schimb.</p>
            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{transfersWithDestChange}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-3 lg:p-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between mb-1">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded flex items-center justify-center">
                <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
              </div>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-600 leading-tight">Luna asta</p>
            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
              {transfers.filter(t => {
                const date = new Date(t.transfer_date);
                const now = new Date();
                return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
              }).length}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-3 lg:p-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between mb-1">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-indigo-100 rounded flex items-center justify-center">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-600" />
              </div>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-600 leading-tight">Șoferi</p>
            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
              {new Set([...transfers.map(t => t.from_driver_name), ...transfers.map(t => t.to_driver_name)]).size}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-3">
        <div className="flex flex-col gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Caută..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-7 sm:pl-9 pr-2 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex items-center gap-1 flex-1">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
              <input
                type="date"
                value={dateFilter.start}
                onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
                className="flex-1 min-w-0 px-1.5 sm:px-2 py-1.5 text-[11px] sm:text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500"
              />
              <span className="text-gray-500 text-xs">-</span>
              <input
                type="date"
                value={dateFilter.end}
                onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
                className="flex-1 min-w-0 px-1.5 sm:px-2 py-1.5 text-[11px] sm:text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500"
              />
            </div>

            <button
              onClick={() => {
                setSearchTerm('');
                setDateFilter({ start: '', end: '' });
              }}
              className="flex items-center justify-center gap-1 px-2 sm:px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
            >
              <Filter className="w-3 h-3" />
              <span className="hidden sm:inline">Reset</span>
            </button>
          </div>
        </div>
      </div>

      {/* Transfers - Cards pe mobil, tabel pe desktop */}
      <div className="space-y-2">
        {/* View pentru mobil (card-uri) */}
        <div className="lg:hidden space-y-2">
          {filteredTransfers.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
              <p className="text-xs text-gray-500">Nu există transferuri</p>
            </div>
          ) : (
            filteredTransfers.map((transfer) => (
              <div key={transfer.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                <div className="space-y-2">
                  {/* Transport ID */}
                  <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                    <span className="font-mono text-xs font-semibold text-gray-900 break-all">
                      {transfer.shipment_id}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getReasonColor(transfer.transfer_reason)}`}>
                      {getReasonLabel(transfer.transfer_reason)}
                    </span>
                  </div>

                  {/* Șoferi */}
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div>
                      <p className="text-gray-500 mb-1">De la:</p>
                      <div className="flex items-center gap-1">
                        <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Users className="w-2.5 h-2.5 text-red-600" />
                        </div>
                        <span className="text-gray-900 truncate text-[10px]">{transfer.from_driver_name || 'N/A'}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-1">Către:</p>
                      <div className="flex items-center gap-1">
                        <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Users className="w-2.5 h-2.5 text-green-600" />
                        </div>
                        <span className="text-gray-900 truncate text-[10px]">{transfer.to_driver_name}</span>
                      </div>
                    </div>
                  </div>

                  {/* Info suplimentare */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-[10px]">
                    <div className="flex items-center gap-1">
                      {transfer.destination_changed ? (
                        <>
                          <MapPin className="w-3 h-3 text-amber-600" />
                          <span className="text-amber-800">Dest. schimbată</span>
                        </>
                      ) : (
                        <span className="text-gray-500">Dest. neschimbată</span>
                      )}
                    </div>
                    <span className="text-gray-600">
                      {new Date(transfer.transfer_date).toLocaleString('ro-RO', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* View pentru desktop (tabel) */}
        <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Transport</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">De la</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Către</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Motiv</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Destinație</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTransfers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                      Nu există transferuri {searchTerm || (dateFilter.start && dateFilter.end) ? 'care să corespundă filtrelor' : ''}
                    </td>
                  </tr>
                ) : (
                  filteredTransfers.map((transfer) => (
                    <tr key={transfer.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-medium text-gray-900">
                          {transfer.shipment_id}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                            <Users className="w-4 h-4 text-red-600" />
                          </div>
                          <span className="text-sm text-gray-900">{transfer.from_driver_name || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <Users className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="text-sm text-gray-900">{transfer.to_driver_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getReasonColor(transfer.transfer_reason)}`}>
                          {getReasonLabel(transfer.transfer_reason)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {transfer.destination_changed ? (
                          <div className="flex items-center gap-1 text-xs">
                            <MapPin className="w-3 h-3 text-amber-600" />
                            <span className="text-amber-800 font-medium">Schimbată</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">Neschimbată</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {new Date(transfer.transfer_date).toLocaleString('ro-RO', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-xs sm:text-sm text-red-800">{error}</p>
        </div>
      )}
    </div>
  );
};

export default AllTransfersPage;